import {TextConfig} from '@mintdata/mint-components/components/text/TextConfig';
import {TextLogic} from '@mintdata/mint-components/components/text/TextLogic';
import {TextModel} from '@mintdata/mint-components/components/text/TextModel';
import {
    BaseComponentWithStateContext,
    componentStateContextTypes
} from '@mintdata/mint-kit/api/components/BaseComponentWithStateContext';
import {Component} from '@mintdata/mint-kit/api/decorators/Component';
import {Event} from '@mintdata/mint-kit/api/decorators/Event';
import {Property} from '@mintdata/mint-kit/api/decorators/Property';
import {AnEvent} from '@mintdata/mint-kit/api/events/EventFn';
import {AFocusEvent, AMouseEvent} from '@mintdata/mint-kit/api/events/EventType';
import {ComponentState} from '@mintdata/mint-kit/api/models/componentState/ComponentState';
import {Overflow} from '@mintdata/mint-kit/api/models/Overflow';
import {TextAlign} from '@mintdata/mint-kit/api/models/TextAlign';
import {TextVerticalAlign} from '@mintdata/mint-kit/api/models/TextVerticalAlign';
import {WordBreak} from '@mintdata/mint-kit/api/models/WordBreak';
import {BaseProps} from '@mintdata/mint-kit/api/properties/BaseProps';
import {PropertyCategory} from '@mintdata/mint-kit/api/properties/PropertyCategory';
import {PropertyPanelTabId} from '@mintdata/mint-kit/api/properties/PropertyPanelTabId';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {ResizeSensor} from 'css-element-queries';
import * as React from 'react';

const styles = require('@mintdata/mint-components/components/text/Text.less');
const cx = classnames.bind(styles);

export class TextProps extends BaseProps
{
    @Property({
        title: '',
        tip: '',
        category: PropertyCategory.Appearance,
        isRequired: false,
        typeConfig: TextConfig,
        typeCtor: TextModel
    })
    model?:TextModel = TextModel.default();

    @Event({isEnabled: true, title: 'onClick', tip: 'Click'})
    onClick?:AnEvent<AMouseEvent>;

    @Event({title: 'onDoubleClick', tip: 'Double click'})
    onDoubleClick?:AnEvent<AMouseEvent>;

    @Event({title: 'onMouseDown', tip: 'Mouse down'})
    onMouseDown?:AnEvent<AMouseEvent>;

    @Event({title: 'onMouseUp', tip: 'Mouse up'})
    onMouseUp?:AnEvent<AMouseEvent>;

    @Event({title: 'onMouseMove', tip: 'Mouse move'})
    onMouseMove?:AnEvent<AMouseEvent>;

    @Event({title: 'onMouseOver', tip: 'Mouse over'})
    onMouseOver?:AnEvent<AMouseEvent>;

    @Event({title: 'onMouseOut', tip: 'Mouse out'})
    onMouseOut?:AnEvent<AMouseEvent>;

    @Event({title: 'onMouseEnter', tip: 'Mouse enter'})
    onMouseEnter?:AnEvent<AMouseEvent>;

    @Event({title: 'onMouseLeave', tip: 'Mouse leave'})
    onMouseLeave?:AnEvent<AMouseEvent>;

    @Event({title: 'onFocused', tip: 'Focused'})
    onFocused?:AnEvent<AFocusEvent>;

    @Event({title: 'onLostFocus', tip: 'Lost focus'})
    onLostFocus?:AnEvent<AFocusEvent>;
}

interface ITextState
{
    isHovered:boolean;
    isActive:boolean;
    resizeSensorHeight:number;
}

@Component<TextProps>({
    name: 'Text',
    tip: 'Text Component',
    linkNameToProp: 'model.text',
    props: TextProps,
    logic: TextLogic,
    width: 200,
    height: 50,
    supportsChildren: false,
    isSvgCompatible: false,
    // requireSize: true,
    exampleFrame: TextModel.exampleFrame()
})
export class Text extends BaseComponentWithStateContext<TextProps, ITextState>
{
    public static readonly childContextTypes = componentStateContextTypes;

    private _resizeSensorElementRef:HTMLDivElement | null = null;
    private _resizeSensor:ResizeSensor;

    public constructor()
    {
        super();

        this.state = {
            isHovered: false,
            isActive: false,
            resizeSensorHeight: null,
        };
    }

    @bind
    private _onClick(event:React.MouseEvent<HTMLElement>):void
    {
        const {onClick} = this.props;

        if( onClick )
            onClick(new AMouseEvent(event));
    }

    @bind
    private _onDoubleClick(event:React.MouseEvent<HTMLElement>):void
    {
        const {onDoubleClick} = this.props;

        if( onDoubleClick )
            onDoubleClick(new AMouseEvent(event));
    }

    @bind
    private _onMouseDown(event:React.MouseEvent<HTMLElement>):void
    {
        const {onMouseDown, model: {activeState}, isDraggable} = this.props;

        if( onMouseDown )
            onMouseDown(new AMouseEvent(event));

        if( isDraggable )
            return;

        this.setState({
            isActive: true
        });

        if( activeState.isEnabled.value && !this.context.isParentActiveEventPropagationEnabled )
            event.stopPropagation();
    }

    @bind
    private _onMouseUp(event:React.MouseEvent<HTMLElement>):void
    {
        const {onMouseUp} = this.props;

        this.setState({
            isActive: false
        });

        if( onMouseUp )
            onMouseUp(new AMouseEvent(event));
    }

    @bind
    private _onMouseMove(event:React.MouseEvent<HTMLElement>):void
    {
        const {onMouseMove} = this.props;

        if( onMouseMove )
            onMouseMove(new AMouseEvent(event));
    }

    @bind
    private _onMouseOver(event:React.MouseEvent<HTMLElement>):void
    {
        const {onMouseOver} = this.props;

        if( onMouseOver )
            onMouseOver(new AMouseEvent(event));
    }

    @bind
    private _onMouseOut(event:React.MouseEvent<HTMLElement>):void
    {
        const {onMouseOut} = this.props;

        if( onMouseOut )
            onMouseOut(new AMouseEvent(event));
    }

    @bind
    private _onMouseEnter(event:React.MouseEvent<HTMLElement>):void
    {
        const {onMouseEnter} = this.props;

        this.setState({
            isHovered: true
        });

        if( onMouseEnter )
            onMouseEnter(new AMouseEvent(event));
    }

    @bind
    private _onMouseLeave(event:React.MouseEvent<HTMLElement>):void
    {
        const {onMouseLeave} = this.props;

        this.setState({
            isHovered: false,
            isActive: false
        });

        if( onMouseLeave )
            onMouseLeave(new AMouseEvent(event));
    }

    @bind
    private _onFocused(event:React.FocusEvent<HTMLElement>):void
    {
        const {onFocused} = this.props;

        if( onFocused )
            onFocused(new AFocusEvent(event));
    }

    @bind
    private _onLostFocus(event:React.FocusEvent<HTMLElement>):void
    {
        const {onLostFocus} = this.props;

        if( onLostFocus )
            onLostFocus(new AFocusEvent(event));
    }

    public componentWillMount():void
    {
        const {model, assets: {fonts: fontsController}} = this.props;

        // fontsController.checkModelAndLoadFont(model);

        const {model: {font, hoverState, activeState}} = this.props;

        // fontsController.loadFont(font.typeface.value.value);
        // fontsController.loadFont(hoverState.font.typeface.value.value);
        // fontsController.loadFont(activeState.font.typeface.value.value);

        fontsController.checkAndLoadFont(font);
        fontsController.checkAndLoadFont(hoverState.font);
        fontsController.checkAndLoadFont(activeState.font);

        // this._checkAndLoadFont(font);
        //
        // if( hoverState.isEnabled.value )
        // 	this._checkAndLoadFont(hoverState.font);
        //
        // if( activeState.isEnabled.value )
        // 	this._checkAndLoadFont(activeState.font);
    }

    public componentWillUnmount():void
    {
        const {
            model: {font, hoverState, activeState},
            assets: {fonts: fontsController}
        } = this.props;

        fontsController.unloadFont(font);
        fontsController.unloadFont(hoverState.font);
        fontsController.unloadFont(activeState.font);

        // fontsController.unloadFont(font.typeface.value.value);
        // fontsController.unloadFont(hoverState.font.typeface.value.value);
        // fontsController.unloadFont(activeState.font.typeface.value.value);
        //
        this._removeResizeSensor();
    }

    public componentDidMount():void
    {
        this._addOrRemoveResizeSensor(this.props);
    }

    componentWillReceiveProps(nextProps:Readonly<TextProps>, nextContext:any):void
    {
        const {model: {withEllipsis, isSingleLine, overflowY}} = this.props;

        // const {
        // 	model: {
        // 		withEllipsis, isSingleLine, overflowY, font,
        // 		hoverState, activeState,
        // 	}
        // } = this.props;

        const {model: newModel} = nextProps;

        if(
            newModel.withEllipsis.value !== withEllipsis.value ||
            newModel.isSingleLine.value !== isSingleLine.value ||
            newModel.overflowY.value !== overflowY.value
        )
        {
            this._addOrRemoveResizeSensor(nextProps);
        }

        const {model: {font, hoverState, activeState}} = this.props;
        const {assets: {fonts: fontsController}} = this.props;

        fontsController.checkAndLoadFont(newModel.font, font);
        fontsController.checkAndLoadFont(newModel.hoverState.font, hoverState.font);
        fontsController.checkAndLoadFont(newModel.activeState.font, activeState.font);

        // fontsController.checkModelAndLoadFont(newModel, this.props.model);

        // this._checkAndLoadFont(newModel.font, font);
        //
        // if( newModel.hoverState.isEnabled.value )
        // 	this._checkAndLoadFont(newModel.hoverState.font, hoverState.font);
        //
        // if( newModel.activeState.isEnabled.value )
        // 	this._checkAndLoadFont(newModel.activeState.font, activeState.font);
    }

    // private _checkAndLoadFont(newFont:Font, oldFont?:Font)
    // {
    // 	const {assets: {fonts: fontsController}} = this.props;
    //
    // 	const newFontName = newFont.typeface.value.toString();
    //
    // 	if( !oldFont || newFontName !== oldFont.typeface.value.toString() )
    // 		fontsController.loadFont(newFontName, true);
    // }

    @bind
    private _addOrRemoveResizeSensor(props:TextProps):void
    {
        const {model: {withEllipsis, isSingleLine, overflowY}} = props;

        if( withEllipsis.value && !isSingleLine.value && Overflow.hidden().isEqualTo(overflowY) )
        {
            if( !this._resizeSensor ) this._addResizeSensor();

            this._onContentResize();
        }
        else
        {
            if( this._resizeSensor ) this._removeResizeSensor();
        }
    }

    @bind
    private _addResizeSensor():void
    {
        if( this._resizeSensorElementRef )
        {
            this._resizeSensor = new ResizeSensor(this._resizeSensorElementRef, this._onContentResize);
        }
    }

    @bind
    private _removeResizeSensor():void
    {
        if( this._resizeSensor && this._resizeSensorElementRef )
        {
            this._resizeSensor.detach(this._onContentResize);
            this._resizeSensor = null;
        }
    }

    @bind
    private _onContentResize():void
    {
        requestAnimationFrame(() =>
        {
            if( !this._resizeSensorElementRef ) return;

            this.setState({resizeSensorHeight: this._resizeSensorElementRef.clientHeight});
        });
    }

    @bind
    private _onResizeSensorElementRef(elementRef:HTMLDivElement):void
    {
        this._resizeSensorElementRef = elementRef;
    }

    render()
    {
        const {
            text,
            textShadow,
            textShadowHover,
            textShadowActive,
            font,
            allowTextSelection,
            isSingleLine,
            withEllipsis,
            wordBreak,
            border,
            background,
            boxShadow,
            textAlign,
            textVerticalAlign,
            padding,
            overflowX = Overflow.hidden(),
            overflowY = Overflow.hidden(),
            hoverState,
            activeState
        } = this.props.model;

        const {cursor} = this.props;

        let value = text ? text.toString() : '';

        const alignSelf:any = TextVerticalAlign.toCssAlignItemsString(textVerticalAlign);
        const justifyContent:any = TextAlign.toCssJustifyContentString(textAlign);

        const userSelect:string = allowTextSelection.value ? 'text' : 'none';
        const isCursorText:boolean = allowTextSelection.value;
        const whiteSpace:string = isSingleLine.value ? 'nowrap' : null;

        let ellipsis:any = {};

        const {resizeSensorHeight} = this.state;

        const wordBreakCss = isSingleLine.value ? null : WordBreak.toCssWordBreakString(wordBreak);

        if( withEllipsis.value )
        {
            if( isSingleLine.value && Overflow.hidden().isEqualTo(overflowX) )
            {
                ellipsis = {
                    overflow: 'hidden',
                    width: '100%',
                    textOverflow: 'ellipsis',
                };
            }
            else if( !isSingleLine.value && Overflow.hidden().isEqualTo(overflowY) )
            {
                let lineClamp:number = Math.floor(resizeSensorHeight / font.lineHeight.value.value);

                ellipsis = {
                    display: '-webkit-box',
                    overflow: 'hidden',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: lineClamp || 1,
                };
            }
        }

        let style:React.CSSProperties = {
            ...font.toCSS(),
            ...background.toCss(),
            ...border.toCSS(),
            overflowX: Overflow.cssValue(overflowX),
            overflowY: Overflow.cssValue(overflowY),
            justifyContent,

            paddingTop: padding && padding.top && padding.top.toString(),
            paddingRight: padding && padding.right && padding.right.toString(),
            paddingBottom: padding && padding.bottom && padding.bottom.toString(),
            paddingLeft: padding && padding.left && padding.left.toString(),
            boxShadow: boxShadow.toCssString(),
            textShadow: textShadow.toCssString(),
            cursor: cursor && !isCursorText && cursor.toCssString(),
        };

        if(
            (this.state.isHovered || this.context.isParentHovered || this.props.propertyPanelSelectedTabId === PropertyPanelTabId.Hover)
            && hoverState.isEnabled.value
        )
            style = {
                ...style,
                ...this._getComponentStateStyle(hoverState),
                textShadow: textShadowHover.toCssString()
            };

        if(
            (this.state.isActive || this.props.propertyPanelSelectedTabId === PropertyPanelTabId.Active)
            && activeState.isEnabled.value
        )
            style = {
                ...style,
                ...this._getComponentStateStyle(activeState),
                textShadow: textShadowActive.toCssString()
            };

        // TODO: MAM-2706
        let paragraphs;
        // if( !isSingleLine.value && font.paragraph.value.value !== 0 )
        // {
        // 	paragraphs = value.split('\n').map((item, i) =>
        // 	{
        // 		return (
        // 			<div
        // 				key={i}
        // 				className={cx('paragraph')}
        // 				style={{marginTop: i ? font.paragraph.toCssString() : 0}}
        // 			>
        // 				{item}
        // 			</div>
        // 		);
        // 	});
        // }

        return (
            <div
                className={cx('base')}
                style={style}
                onClick={this._onClick}
                onDoubleClick={this._onDoubleClick}
                onMouseDown={this._onMouseDown}
                onMouseUp={this._onMouseUp}
                onMouseMove={this._onMouseMove}
                onMouseOver={this._onMouseOver}
                onMouseOut={this._onMouseOut}
                onMouseEnter={this._onMouseEnter}
                onMouseLeave={this._onMouseLeave}
                onFocus={this._onFocused}
                onBlur={this._onLostFocus}
                ref={this._onResizeSensorElementRef}
            >
                <div
                    className={cx({isCursorText})}
                    data-cursor-override="true"
                    style={{
                        flex: 1,
                        alignSelf,
                        textAlign: textAlign.toString(),
                        userSelect,
                        whiteSpace,
                        ...ellipsis,
                        wordBreak: wordBreakCss,
                    }}
                >
                    {paragraphs ? paragraphs : value}
                </div>
            </div>
        );
    }

    private _getComponentStateStyle(state:ComponentState):React.CSSProperties
    {
        const {font, background, border, boxShadow} = state;

        return {
            ...font.toCSS(),
            ...background.toCss(),
            ...border.toCSS(),
            boxShadow: boxShadow.toCssString()
        };
    }
}
